import { Box, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Markdown, { RuleType } from 'markdown-to-jsx'
import MarkdownCustomPreElement from './MarkdownCustomPreElement'


const markdownParserOptions = {
    disableParsingRawHTML: true,
    renderRule: ( next, node ) => {
        if ( node.type === RuleType.codeBlock ) {
            return (
                <MarkdownCustomPreElement
                    key={node.text.slice( 0, 20 )}
                    language={node.lang || 'Text'}
                    content={node.text}
                />
            )
        }
        return next()
    },
    overrides: {
        pre: {
            component: MarkdownCustomPreElement,
            props: {
                className: 'markdown-code-pre'
            }
        }
    }
}


const CustomMarkdown = ( {
    content = '',
    typeWriterEffect = false,
    speed = 40,
    chunkSize = 5,
    scrollToBottom = false,
    scrollContainer,
    onTypingCompleted
} ) => {
    const { theme } = useTheme()

    const [displayContent, setDisplayContent] = useState(
        typeWriterEffect ? '' : content
    )

    const indexRef = useRef( 0 )
    const timerRef = useRef( null )

    const words = useMemo( () => content.split( ' ' ), [content] )


    useEffect( () => {
        clearTimeout( timerRef.current )
        indexRef.current = 0
        setDisplayContent( typeWriterEffect ? '' : content )
    }, [content, typeWriterEffect] )


    useEffect( () => {
        if ( !typeWriterEffect ) return
        if ( indexRef.current >= words.length ) {
            onTypingCompleted?.()
            return
        }

        timerRef.current = setTimeout( () => {
            const nextIndex = Math.min(
                indexRef.current + chunkSize,
                words.length
            )

            const nextChunk = words.slice( indexRef.current, nextIndex ).join( ' ' )

            indexRef.current = nextIndex

            setDisplayContent( prev =>
                prev ? `${prev} ${nextChunk}` : nextChunk
            )

            if ( scrollToBottom && scrollContainer?.current && scrollContainer.current.scrollTop + scrollContainer.current.clientHeight < scrollContainer.current.scrollHeight ) {
                scrollContainer.current.scrollTop = scrollContainer.current.scrollHeight
            }
        }, speed )

        return () => clearTimeout( timerRef.current )
    }, [
        displayContent,
        words,
        speed,
        chunkSize,
        typeWriterEffect,
        scrollToBottom,
        scrollContainer,
        onTypingCompleted
    ] )


    return (
        <Box className={`custom-markdown ${theme}`} sx={{
            fontSize: "14px"
        }}>
            <Markdown options={markdownParserOptions}>
                {displayContent}
            </Markdown>
        </Box>
    );
}

export default CustomMarkdown
